/*
 *  Generic Form Tracking
 *
 *   submit_form for all forms:
 *   formId  -> "<form-id>:success" | "<form-id>:error" | "<form-id>:unknown"
 *   elements -> form fields and values, with an extra "<form-id>_error"
 *   entry carrying "<field>:<message>" when validation errors exist
*/
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';

const SUBMIT_FORM = 'iglu:com.snowplowanalytics.snowplow/submit_form/jsonschema/1-0-0';
const NL_FIELD = /^(newsletterList|newsletterSubscriptionList)$/;
const SKIP_FIELD = /captcha-response$|^contact_url_confirm$/;
const ERROR_SELECTOR =
  '.eln-newsletter-form-p, .SignupNewsletter-NewsletterError, [id*="error" i]';

const WAIT_MS = 5000;  
const ERROR_MS = 800;  
const MAX_LEN = 4096;

const suppressed = new WeakSet();
const statusListeners = [];
let dataLayerPatched = false;

const cls = (el) => (typeof el.className === 'string' ? el.className : '');
const cap = (s) => (s.length > MAX_LEN ? s.substring(0, MAX_LEN) : s);

export function isNewsletterForm(form) {
  if (!form || !form.elements) return false;
  return Array.prototype.some.call(form.elements, (el) => NL_FIELD.test(el.name || ''));
}

export const isNativeTrackable = (form) => !suppressed.has(form);

function getFormId(container) {
  return (
    container.id ||
    container.getAttribute('data-form-id') ||
    container.getAttribute('name') ||
    (isNewsletterForm(container) ? 'iiris_nl' : 'iiris_form')
  );
}

function collectFields(form, filter, transform) {
  let out = [];
  Array.prototype.forEach.call(form.elements, (el) => {
    const nodeName = el.nodeName;
    if (nodeName !== 'INPUT' && nodeName !== 'TEXTAREA' && nodeName !== 'SELECT') return;

    const type = (el.type || 'text').toLowerCase();
    if (type === 'submit' || type === 'image' || type === 'password') return;

    const name =  el.id || el.name || type || nodeName;
    if (SKIP_FIELD.test(name)) return;
    if (filter && !filter(el)) return;

    let value = (type === 'checkbox' || type === 'radio') && !el.checked ? null : el.value;
    if (value !== null && transform) value = transform(value, { name, value, nodeName }, el);

    const entry = { name, value: value === undefined ? null : value, nodeName };
    if (nodeName === 'INPUT') entry.type = type;
    out.push(entry);
  });
  return out;
}

function errorField(p) {
  for (let n = p.parentElement, i = 0; n && i < 3; n = n.parentElement, i++) {
    const ctl = n.querySelector('input, select, textarea');
    if (!ctl) continue;
    if (/captcha/i.test(ctl.name || '')) return 'captcha';
    if (ctl.name) return ctl.name;
  }
  return 'form';
}

function collectErrors(form) {
  let out = [];
  Array.prototype.forEach.call(form.querySelectorAll(ERROR_SELECTOR), (p) => {
    if (!p.getClientRects().length) return;
    const text = (p.textContent || '').trim();
    if (text) out.push(errorField(p) + ':' + text);
  });
  return out;
}

function watchDataLayer() {
  if (dataLayerPatched) return;
  dataLayerPatched = true;

  const dataLayer = (window.dataLayer = window.dataLayer || []);
  const originalPush = dataLayer.push;

  dataLayer.push = function (...args) {
    args.forEach((entry) => {
      const data = entry && entry.event === 'newsletter' && entry.data;
      console.log('line 97 inside watch data layer', data)
      if (!data || !data.status) return;
      statusListeners.slice().forEach((fn) => {
        try { fn(data.status, data); } catch (e) {}
      });
    });
    return originalPush.apply(this, args);
  };
}

function watchBusinessEvents() {
  window.addEventListener('trackFormSubmitEvt', (evt) => {
    const data = evt.detail;
    if (!data || !data.formId || !['success', 'error'].includes(data.status)) return;
    console.log('line 111 inside trackFormSubmitEvt ', data)
    statusListeners.slice().forEach((fn) => {
      try { fn(data.status, data); } catch (e) {}
    });
  });
}


function emit(form, elements, status, errors) {
  const els = elements.slice();
  if (errors && errors.length) {
    els.push({
      name: `${getFormId(form)}_error`,
      value: cap(errors.join('|')),
      nodeName: 'INPUT',
      type: 'hidden',
    });
  }

  trackSelfDescribingEvent({
    event: {
      schema: SUBMIT_FORM,
      data: {
        formId: `${getFormId(form)}:${status}`,
        formClasses: cls(form).match(/\S+/g) || [],
        elements: els,
      },
    },
  });
}

export function enableGenericFormTracking(options = {}) {
  if (window.__iirisFormEnabled) return;
  window.__iirisFormEnabled = true;
  watchDataLayer();
  watchBusinessEvents()

  window.addEventListener('submit', function (evt) {
    const form = evt.target;
    if (!form || form.nodeName !== 'FORM') return;

    let elements;
    try {
      elements = collectFields(form, options.filter, options.transform);
    } catch (e) {
      return;
    }

    suppressed.add(form);
    setTimeout(() => suppressed.delete(form), 0);

    let errors = [];
    let done = false;

    const finish = (status) => {
      if (done) return;
      done = true;
      const i = statusListeners.indexOf(onStatus);
      if (i !== -1) statusListeners.splice(i, 1);
      clearTimeout(errorTimer);
      clearTimeout(waitTimer);
      emit(form, elements, status, status === 'error' ? errors : null);
    };

const onStatus = (status, data) => {
  if (isNewsletterForm(form)) {
    console.log('line 177 isNewsletterForm',isNewsletterForm )
    if (status === 'Submit Success') finish('success');
    else if (status === 'Submit Fail') finish('error');
  } else if (data && data.formId === getFormId(form)) {
    console.log('line 177 ',isNewsletterForm , status)
    finish(status);
  }
};
    statusListeners.push(onStatus);

    const errorTimer = setTimeout(() => { errors = collectErrors(form); }, ERROR_MS);

    const waitTimer = setTimeout(() => finish(errors.length ? 'error' : 'unknown'), WAIT_MS);
  }, true);
}
